import { Fragment, useState, useEffect, useCallback } from "react";
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Check,
  X,
  AlertTriangle,
  Hourglass,
  Ban,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import type { ToolState } from "../types";
import { MessageResponse } from "@/components/ai-elements/message";

// --- Public types ---

export interface ToolCallCardProps {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  state: ToolState | undefined;
  rawArgString?: string;
  progressLines?: string[];
  result?: unknown;
  error?: string;
  durationMs?: number;
  startedAt?: number;
  /** When true, collapse the panel (e.g. when text/content appears after this tool). */
  hasContentAfter?: boolean;
}

// --- Helpers ---

const STATE_META: Record<
  string,
  { label: string; color: string; icon: React.ElementType; defaultOpen: boolean }
> = {
  "input-streaming": { label: "Pending", color: "text-blue-500 bg-blue-500/10 border-blue-500/30", icon: Hourglass, defaultOpen: true },
  "input-available":  { label: "Running", color: "text-yellow-500 bg-yellow-500/10 border-yellow-500/30", icon: Loader2, defaultOpen: true },
  "output-available": { label: "Completed", color: "text-green-500 bg-green-500/10 border-green-500/30", icon: Check, defaultOpen: false },
  "output-error":     { label: "Error", color: "text-red-500 bg-red-500/10 border-red-500/30", icon: X, defaultOpen: true },
  "approval-requested": { label: "Awaiting Approval", color: "text-orange-500 bg-orange-500/10 border-orange-500/30", icon: AlertTriangle, defaultOpen: true },
  "approval-responded": { label: "Approved", color: "text-green-500 bg-green-500/10 border-green-500/30", icon: Check, defaultOpen: false },
  "output-denied":    { label: "Denied", color: "text-zinc-400 bg-zinc-400/10 border-zinc-400/30", icon: Ban, defaultOpen: false },
};

function getStateMeta(state: ToolState | undefined) {
  return STATE_META[state ?? "input-available"] ?? STATE_META["input-available"];
}

const PREVIEW_KEYS = ["query", "url", "code", "sql", "filePath", "prompt", "message", "text", "input"];
const PREVIEW_HEIGHT_REM = 5;

function getResultPreviewText(result: unknown, error?: string): string {
  if (error) return error;
  if (result === undefined || result === null) return "";
  return typeof result === "string"
    ? result
    : JSON.stringify(result, null, 2);
}

function getToolSummary(toolName: string, args: Record<string, unknown>): string {
  for (const key of PREVIEW_KEYS) {
    if (args[key] && typeof args[key] === "string") {
      const val = args[key] as string;
      return val.length > 60 ? val.slice(0, 57) + "..." : val;
    }
  }
  const keyCount = Object.keys(args).length;
  return keyCount > 0 ? `${keyCount} parameter${keyCount > 1 ? "s" : ""}` : "";
}

function isSimpleArgs(args: Record<string, unknown>): boolean {
  const keys = Object.keys(args);
  return (
    keys.length <= 5 &&
    keys.every(
      (k) =>
        typeof args[k] === "string" ||
        typeof args[k] === "number" ||
        typeof args[k] === "boolean",
    )
  );
}

// --- Duration Timer ---

function DurationTimer({
  startedAt,
  durationMs,
  isActive,
}: {
  startedAt?: number;
  durationMs?: number;
  isActive: boolean;
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isActive || !startedAt) return;
    const tick = () => setElapsed(Date.now() - startedAt);
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [startedAt, isActive]);

  const display = durationMs ?? elapsed;
  if (!display && !isActive) return null;

  return (
    <span className="text-xs tabular-nums text-muted-foreground">
      {(display / 1000).toFixed(1)}s
    </span>
  );
}

// --- Argument display ---

function ToolArgDisplay({
  args,
  rawArgString,
  isStreaming,
}: {
  args: Record<string, unknown>;
  rawArgString?: string;
  isStreaming: boolean;
}) {
  if (isStreaming && rawArgString) {
    return (
      <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-xs">
        {rawArgString}
        <span className="inline-block h-3.5 w-1 animate-cursor-blink bg-foreground align-middle" />
      </pre>
    );
  }

  const keys = Object.keys(args);
  if (keys.length === 0) return null;

  if (isSimpleArgs(args)) {
    return (
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
        {keys.map((key) => (
          <Fragment key={key}>
            <span className="font-medium text-muted-foreground">{key}</span>
            <span className="truncate font-mono">{JSON.stringify(args[key])}</span>
          </Fragment>
        ))}
      </div>
    );
  }

  return (
    <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-xs">
      {JSON.stringify(args, null, 2)}
    </pre>
  );
}

// --- Result display ---

function ToolResultDisplay({
  result,
  error,
  progressLines,
}: {
  result?: unknown;
  error?: string;
  progressLines?: string[];
}) {
  if (error) {
    return (
      <div className="mt-2 rounded border border-red-500/30 bg-red-500/5 p-2 text-xs text-red-600 dark:text-red-400">
        <AlertTriangle className="mr-1 inline-block h-3 w-3" />
        {error}
      </div>
    );
  }

  if (progressLines && progressLines.length > 0 && result === undefined) {
    return (
      <div className="mt-2 space-y-1 border-t pt-2 text-xs text-muted-foreground">
        {progressLines.map((line, i) => (
          <div key={i} className="flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {line}
          </div>
        ))}
      </div>
    );
  }

  if (result === undefined || result === null) return null;

  if (typeof result === "string") {
    return (
      <div className="mt-2 border-t pt-2">
        <MessageResponse>{result}</MessageResponse>
      </div>
    );
  }

  return (
    <div className="mt-2 border-t pt-2">
      <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-xs">
        {JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
}

// --- Main Component ---

export function ToolCallCard({
  toolName,
  args,
  state,
  rawArgString,
  progressLines,
  result,
  error,
  durationMs,
  startedAt,
  hasContentAfter = false,
}: ToolCallCardProps) {
  const meta = getStateMeta(state);
  const defaultOpen =
    state === "output-available" && !hasContentAfter ? true : meta.defaultOpen;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const Icon = meta.icon;
  const summary = getToolSummary(toolName, args);
  const isActive = state === "input-available" || state === "input-streaming";
  const isStreaming = state === "input-streaming";

  useEffect(() => {
    if (state === "output-error" || state === "approval-requested") {
      setIsOpen(true);
    } else if (state === "output-available" && hasContentAfter) {
      setIsOpen(false);
    }
  }, [state, hasContentAfter]);

  const handleCopyArgs = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(args, null, 2));
    toast.success("Copied arguments");
  }, [args]);

  return (
    <div className={`my-2 rounded-lg border ${meta.color.split(" ").slice(1).join(" ")} overflow-hidden`}>
      {/* Header */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/50 transition-colors"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}

        <Icon
          className={`h-3.5 w-3.5 shrink-0 ${meta.color.split(" ")[0]} ${
            state === "input-available" ? "animate-spin" : ""
          }`}
        />

        <span className="font-medium">{toolName}</span>

        {!isOpen && summary && (
          <span className="truncate text-xs text-muted-foreground">
            {summary}
          </span>
        )}

        <span className="ml-auto flex items-center gap-2">
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${meta.color}`}>
            {meta.label}
          </span>
          <DurationTimer startedAt={startedAt} durationMs={durationMs} isActive={isActive} />
        </span>
      </button>

      {/* Collapsed preview: 10rem height when hasContentAfter */}
      {!isOpen && hasContentAfter && (result !== undefined || error) && (
        <div
          className="cursor-pointer overflow-hidden border-t px-3 py-2 text-xs text-muted-foreground hover:bg-accent/30 transition-colors"
          style={{ height: `${PREVIEW_HEIGHT_REM}rem` }}
          onClick={() => setIsOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsOpen(true);
            }
          }}
        >
          <pre className="overflow-x-auto overflow-y-auto whitespace-pre-wrap font-mono">
            {getResultPreviewText(result, error)}
          </pre>
        </div>
      )}

      {/* Body */}
      {isOpen && (
        <div className="border-t px-3 py-2 text-sm">
          {/* Input section */}
          {Object.keys(args).length > 0 && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Input
                </span>
                <button
                  type="button"
                  className="rounded p-0.5 text-muted-foreground hover:text-foreground"
                  onClick={handleCopyArgs}
                  title="Copy arguments"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
              <ToolArgDisplay args={args} rawArgString={rawArgString} isStreaming={isStreaming} />
            </div>
          )}

          {/* Progress / Result / Error */}
          <ToolResultDisplay
            result={result}
            error={error}
            progressLines={progressLines}
          />
        </div>
      )}
    </div>
  );
}
